"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useStore } from "zustand";
import { Store } from "@/actions/schemas/stores";
import { createVitrineStore, type VitrineStore } from "@/stores/vitrine-store";

export type VitrineStoreApi = ReturnType<typeof createVitrineStore>;

const VitrineStoreContext = createContext<VitrineStoreApi | undefined>(
  undefined
);

export type VitrineProviderProps = {
  store: Store;
  identifier: string;
  children: ReactNode;
};

export const VitrineProvider = ({
  store,
  identifier,
  children,
}: VitrineProviderProps) => {
  const storeRef = useMemo(
    () => createVitrineStore({ ...store, identifier }),
    [store, identifier]
  );

  return (
    <VitrineStoreContext.Provider value={storeRef}>
      {children}
    </VitrineStoreContext.Provider>
  );
};

export const useVitrineStore = <T,>(
  selector: (store: VitrineStore) => T
): T => {
  const vitrineStoreContext = useContext(VitrineStoreContext);

  if (!vitrineStoreContext) {
    throw new Error("useVitrineStore must be used within VitrineProvider");
  }

  return useStore(vitrineStoreContext, selector);
};
